import { inject, ElementRef, signal, Renderer2, effect } from '@angular/core';

/**
 * @internal
 * Normalizes the given options.
 *
 * @param options - The options to normalize
 * @returns The normalized options
 */
const normalizeUseAttributeOptions = (options) => ({
    ...(options ?? {}),
    target: options?.target ?? inject(ElementRef).nativeElement
});
/**
 * @internal
 * Normalizes the given options.
 *
 * @param options - The options to normalize
 * @returns The normalized options
 */
const normalizeBindAttributeOptions = (options) => ({
    ...(options ?? {}),
    target: options?.target ?? inject(ElementRef).nativeElement
});
/**
 * Creates a signal that binds its value as an attribute on the host element or a different target element.
 *
 * @example Default usage
 *
 * By default, this composable will read the value of the attribute from the usage in the template
 *
 * ```ts
 * import { useAttribute } from '@bynary/composables/attribute';
 *
 * @Component({
 *     selector: 'my-component'
 * })
 * class MyComponent {
 *
 *     label = useAttribute('label');
 * }
 * ```
 * ```html
 * <my-component #myComponent label="foo"></my-component>
 * ```
 *
 * This will output:
 *
 * ```html
 * <my-component label="foo"></my-component>
 * ```
 *
 * @example Programmatically setting the value
 *
 * You can also change the value of the attribute programmatically by using the returned signal:
 *
 * ```ts
 * import { useAttribute } from '@bynary/composables/attribute';
 *
 * @Component({
 *     selector: 'my-component'
 * })
 * class MyComponent {
 *
 *     label = useAttribute('label');
 *
 *     constructor() {
 *         this.label.set('programmatically set value');
 *     }
 * }
 * ```
 * ```html
 * <my-component #myComponent></my-component>
 * ```
 *
 * This will output:
 *
 * ```html
 * <my-component label="programmatically set value"></my-component>
 * ```
 *
 * @param attributeName - The name of the attribute
 * @param options - A set of {@link IUseAttributeOptions options}
 * @returns A signal holding the value of the attribute
 */
function useAttribute(attributeName, options) {
    const { namespace, initialValue, defaultValue, target } = normalizeUseAttributeOptions(options);
    const initialAssignedValue = target.getAttributeNS(namespace ?? null, attributeName);
    const value = signal((typeof initialValue !== 'undefined' ? initialValue : initialAssignedValue) ?? defaultValue);
    return bindAttribute(attributeName, value, { namespace, defaultValue, target });
}
/**
 * Binds an attribute to the host element or a different target element. Similar to `useAttribute`, but accepts a signal as an input instead of creating a new one and won't read the value from the template.
 * Will return the signal that has been passed in.
 *
 * @example
 * ```ts
 * import { bindAttribute } from '@bynary/composables/attribute';
 *
 * @Component({
 *     selector: 'my-component'
 * })
 * class MyComponent {
 *
 *     label = signal('foo');
 *
 *     constructor() {
 *         bindAttribute('label', this.label);
 *     }
 * }
 * ```
 *
 * This will output:
 *
 * ```html
 * <my-component label="label"></my-component>
 * ```
 *
 * @param attributeName - The name of the attribute
 * @param value - The signal whose value should be bound
 * @param options - A set of {@link IBindAttributeOptions options}
 * @returns The passed in signal (`value` parameter)
 */
const bindAttribute = (attributeName, value, options) => {
    const { namespace, defaultValue, target } = normalizeBindAttributeOptions(options);
    const renderer = inject(Renderer2);
    effect(() => {
        const currentValue = value();
        const newValue = typeof currentValue !== 'undefined' ? currentValue : defaultValue;
        if (newValue != null) {
            renderer.setAttribute(target, attributeName, newValue, namespace);
        }
        else {
            renderer.removeAttribute(target, attributeName, namespace);
        }
    });
    return value;
};

/**
 * Normalizes the given options.
 *
 * @param options - The options to normalize
 * @internal
 */
const normalizeUseBooleanAttributeOptions = (options) => ({
    ...(options ?? {}),
    target: options?.target ?? inject(ElementRef).nativeElement
});
/**
 * Normalizes the given options.
 *
 * @param options - The options to normalize
 * @internal
 */
const normalizeBindBooleanAttributeOptions = (options) => options ?? {};
/**
 * @internal
 * Converts the given value to an attribute value.
 * Boolean Attributes are set, when the value is an empty string and removed, when the value is `null` or `undefined`.
 *
 * @param value - A boolean value
 * @returns
 *  an empty string, when the attribute should be set,
 * `null` when the attribute should be explicitly removed,
 * `undefined` when the attribute may be overridden
 */
const toAttributeValue = (value) => {
    if (value === true) {
        return '';
    }
    else if (value === false) {
        return null;
    }
    return undefined;
};
/**
 * @internal
 * Converts the given attribute value to boolean.
 * A boolean attribute is considered `true`, when the value is defined.
 *
 * @param value - The attributes value
 * @returns
 *  `true` when the attribute value is defined
 *  `false` when the attribute value is `null` or `undefined`
 */
const fromAttributeValue = (value) => {
    return value != null;
};
/**
 * Creates a signal that binds its value as a boolean attribute on the host element or a different target element.
 *
 * @example
 * ```ts
 * import { useBooleanAttribute } from '@bynary/composables/attribute';
 *
 * @Component({
 *     selector: 'my-component'
 * })
 * class MyComponent {
 *
 *     isDisabled = useBooleanAttribute('disabled');
 * }
 * ```
 *
 * @param attributeName - The name of the attribute
 * @param options - A set of {@link IUseBooleanAttributeOptions options}
 * @returns A signal holding the current value of the attribute as a boolean
 */
const useBooleanAttribute = (attributeName, options) => {
    const { namespace, initialValue, defaultValue, target } = normalizeUseBooleanAttributeOptions(options);
    const attributeValue = fromAttributeValue(target.getAttributeNS(namespace ?? null, attributeName));
    const value = signal(initialValue ?? attributeValue ?? defaultValue);
    return bindBooleanAttribute(attributeName, value, {
        namespace,
        defaultValue,
        target
    });
};
/**
 * Binds the value of the given signal as a boolean attribute on the host element or a different target element.
 *
 * @example
 * ```ts
 * import { bindBooleanAttribute } from '@bynary/composables/attribute';
 *
 * @Component({
 *     selector: 'my-component'
 * })
 * class MyComponent {
 *
 *     isDisabled = signal(false);
 *
 *     constructor() {
 *         bindBooleanAttribute('disabled', this.isDisabled);
 *     }
 * }
 * ```
 *
 * @param attributeName - The name of the attribute
 * @param value - The signal to bind
 * @param options - A set of {@link IBindBooleanAttributeOptions options}
 * @returns The passed in signal (`value` parameter)
 */
const bindBooleanAttribute = (attributeName, value, options) => {
    const { namespace, defaultValue, target } = normalizeBindBooleanAttributeOptions(options);
    const attribute = useAttribute(attributeName, { namespace, target });
    const defaultAttributeValue = toAttributeValue(defaultValue);
    effect(() => {
        attribute.set(toAttributeValue(value()) ?? defaultAttributeValue);
    }, { allowSignalWrites: true });
    return value;
};

/**
 * @packageDocumentation
 * Composables to bind attributes on elements.
 *
 * @example
 * ```ts
 * @Component({
 *    selector: 'my-component',
 *    template: '<ng-content></ng-content>'
 * })
 * class MyButton {
 *     isDisabled = useBooleanAttribute('disabled');
 *
 *     constructor() {
 *         bindAttribute('tab-index', computed(() => this.isDisabled() ? '-1' : '0'));
 *     }
 * }
 * ```
 *
 * @module @bynary/composables/attribute
 */

/**
 * Generated bundle index. Do not edit.
 */

export { bindAttribute, bindBooleanAttribute, useAttribute, useBooleanAttribute };
